import { SvgRefresh } from '@bazaar-marketplace/assets/refresh';
import { SvgStickerCircle } from '@bazaar-marketplace/assets/sticker-circle';
import { cn } from '@bazaar-marketplace/utils';
import { useTranslations } from 'next-intl';
import { useCallback, useEffect, useRef, useState } from 'react';
import { match } from 'ts-pattern';
import { AddToCollectionButton, Button, Sheet, SheetContent, SheetTrigger } from '~/components';
import { usePacksControllerGetPackById, useStickersControllerCreateStickersPack } from '~/lib/api';
import { MintFlow } from '~/module/mint/mint-flow';
import { WithdrawNftFlow } from '~/module/nft/withdraw-nft-flow';
import { PartnerMarketplacesSheet } from '~/module/pack/partner-marketplaces-sheet';
import { RefreshStickersSheet } from '~/module/pack/refresh-stickers-sheet';
import { FeatureFlag, useFeatureFlags } from '~/providers/feature-flag-provider';

interface PackActionsProps {
  packId: string;
  onUnwrapRequest?: (packId: string) => void;
  onCreateStickerSetRequest?: (packId: string) => void;
  refetchPack?: () => void;
}

export function PackActions(props: PackActionsProps) {
  const intervalRef = useRef<number | null>(null);

  const pack = usePacksControllerGetPackById(props.packId).data?.data;

  const t = useTranslations('my-collection.nft.actions');
  const [mintSheetOpen, setMintSheetOpen] = useState(false);
  const [withdrawSheetOpen, setWithdrawSheetOpen] = useState(false);
  const [partnerMarketplacesSheetOpen, setPartnerMarketplacesSheetOpen] = useState(false);
  const [refreshStickersSheetOpen, setRefreshStickersSheetOpen] = useState(false);
  const { isFeatureEnabled } = useFeatureFlags();
  const nftFeaturesHidden = !isFeatureEnabled(FeatureFlag.NFT_FEATURES);
  const partnerMarketplacesHidden = !isFeatureEnabled(FeatureFlag.PARTNER_MARKETPLACES);
  const isNonTradeable = pack?.isNonTradeable ?? false;

  const [isPending, setIsPending] = useState(pack?.unwrapped?.stickerPackStatus === 'PENDING');

  const { mutateAsync: createStickerSetForPack } = useStickersControllerCreateStickersPack();

  const isMinted = pack?.nft?.nftMintStatus === 'DONE';
  const isMinting = pack?.nft?.nftMintStatus === 'PENDING';

  const canMintCollection = pack?.unwrapped ? pack.unwrapped.isMintable : false;
  const canMintBlindbox = !pack?.unwrapped;
  const canMint = (canMintCollection || canMintBlindbox) && !isMinted && !isMinting;

  const refetchMemoized = useCallback(() => {
    props.refetchPack?.();
  }, [props]);

  // Sync local pending state when pack status changes
  useEffect(() => {
    setIsPending(pack?.unwrapped?.stickerPackStatus === 'PENDING');
  }, [pack?.unwrapped?.stickerPackStatus]);

  // Auto-refetch when sticker pack is pending
  useEffect(() => {
    if (!isPending) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      return;
    }

    const REFETCH_INTERVAL_MS = 3000;
    intervalRef.current = window.setInterval(() => {
      refetchMemoized();
    }, REFETCH_INTERVAL_MS);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [isPending, refetchMemoized]);

  function renderPrimaryAction() {
    if (!pack) return null;

    return match(pack)
      .with({ unwrapped: { stickerPackStatus: 'CREATED' } }, () => (
        <div className="flex gap-2 items-center">
          <AddToCollectionButton
            className="flex-1"
            telegramStickerSetUrl={pack.unwrapped?.telegramStickerSetUrl ?? undefined}
          />
          <Sheet open={refreshStickersSheetOpen} onOpenChange={setRefreshStickersSheetOpen}>
            <SheetTrigger asChild>
              <Button
                aria-label={t('refresh-stickers.button')}
                className="p-0 flex-shrink-0 size-16"
                variant="dark-bordered"
              >
                <SvgRefresh className="size-5" />
              </Button>
            </SheetTrigger>
            <RefreshStickersSheet
              packId={pack.id}
              onSuccess={() => {
                setRefreshStickersSheetOpen(false);
                props.refetchPack?.();
              }}
            />
          </Sheet>
        </div>
      ))
      .with({ unwrapped: { stickerPackStatus: 'PENDING' } }, () => (
        <Button className="w-full" disabled variant="rainbow">
          <span className="flex gap-2 items-center animate-pulse">
            <SvgStickerCircle /> {t('creating-sticker-pack')}
          </span>
        </Button>
      ))
      .with({ unwrapped: {} }, () => {
        const effectiveStatus = isPending ? 'PENDING' : pack.unwrapped?.stickerPackStatus;

        return match(effectiveStatus)
          .with('CREATED', () => (
            <div className="flex gap-2 items-center">
              <AddToCollectionButton
                className="flex-1"
                telegramStickerSetUrl={pack.unwrapped?.telegramStickerSetUrl ?? undefined}
              />
              <Sheet open={refreshStickersSheetOpen} onOpenChange={setRefreshStickersSheetOpen}>
                <SheetTrigger asChild>
                  <Button
                    aria-label={t('refresh-stickers.button')}
                    className="p-0 flex-shrink-0 size-16"
                    variant="dark-bordered"
                  >
                    <SvgRefresh className="size-5" />
                  </Button>
                </SheetTrigger>
                <RefreshStickersSheet
                  packId={pack.id}
                  onSuccess={() => {
                    setRefreshStickersSheetOpen(false);
                    props.refetchPack?.();
                  }}
                />
              </Sheet>
            </div>
          ))
          .with('PENDING', () => (
            <Button className="w-full" disabled variant="rainbow">
              <span className="flex gap-2 items-center animate-pulse">
                <SvgStickerCircle /> {t('creating-sticker-pack')}
              </span>
            </Button>
          ))
          .otherwise(() => (
            <Button
              className="w-full"
              variant="rainbow"
              onClick={() => {
                setIsPending(true);
                props.refetchPack?.();

                void createStickerSetForPack({
                  data: {
                    packId: pack.id,
                  },
                }).catch(() => {
                  setIsPending(false);
                });
              }}
            >
              <SvgStickerCircle />
              {t('create-sticker-pack')}
            </Button>
          ));
      })
      .otherwise(() => (
        // Unopened pack
        <Button
          variant="rainbow"
          onClick={() => {
            props.onUnwrapRequest?.(pack.id);
          }}
        >
          <SvgStickerCircle /> {t('open-box')}
        </Button>
      ));
  }

  function renderNftAction() {
    if (!pack) return null;

    if (nftFeaturesHidden) {
      return (
        <div className="relative">
          <Button className="w-full" disabled variant="dark-bordered">
            {t('mint')}
          </Button>
          <div className="absolute right-1.5 top-1.5 text-[#362D4B] z-20 bg-white w-14 rounded-lg h-5 text-[10px] text-center whitespace-nowrap font-bold flex items-center justify-center">
            {t('soon')}
          </div>
        </div>
      );
    }

    if (isMinted) {
      return (
        <Sheet open={withdrawSheetOpen} onOpenChange={setWithdrawSheetOpen}>
          <SheetTrigger asChild>
            <Button variant="dark-bordered">{t('withdraw')}</Button>
          </SheetTrigger>
          <SheetContent className="rounded-t-3xl">
            <WithdrawNftFlow
              pack={pack}
              onSuccess={() => {
                setWithdrawSheetOpen(false);
              }}
            />
          </SheetContent>
        </Sheet>
      );
    }

    if (!canMint) return null;

    return (
      <Sheet open={mintSheetOpen} onOpenChange={setMintSheetOpen}>
        <SheetTrigger asChild>
          <Button disabled={!canMint} variant="dark-bordered">
            {t('mint')}
          </Button>
        </SheetTrigger>
        <SheetContent className="rounded-t-3xl">
          <MintFlow
            pack={pack}
            onSuccess={() => {
              setMintSheetOpen(false);
            }}
          />
        </SheetContent>
      </Sheet>
    );
  }

  const nftAction = renderNftAction();

  return (
    <div className="flex flex-col gap-2">
      {renderPrimaryAction()}

      <div className={cn('grid gap-2', nftAction === null ? 'grid-cols-1' : 'grid-cols-2')}>
        {nftAction}
        {partnerMarketplacesHidden || isNonTradeable ? (
          <div className="relative">
            <Button className="w-full" disabled variant="dark-bordered">
              {t('trade')}
            </Button>
            {isNonTradeable ? null : (
              <div className="absolute right-1.5 top-1.5 text-[#362D4B] z-20 bg-white w-14 rounded-lg h-5 text-[10px] text-center whitespace-nowrap font-bold flex items-center justify-center">
                {t('soon')}
              </div>
            )}
          </div>
        ) : (
          <Sheet open={partnerMarketplacesSheetOpen} onOpenChange={setPartnerMarketplacesSheetOpen}>
            <SheetTrigger asChild>
              <Button className="w-full" variant="dark-bordered">
                {t('trade')}
              </Button>
            </SheetTrigger>
            <SheetContent className="rounded-t-3xl">
              <PartnerMarketplacesSheet mintedNft={isMinted} />
            </SheetContent>
          </Sheet>
        )}
      </div>
    </div>
  );
}
